import { useState } from "react";
import { Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FormErrorMessage } from "../../components";
import { InputFieldValidationErrorMessage } from "../../configs";
import { useAppSelector, VRIAppStateType } from "../../core";
import {
  ApiSchemaCreateVendor,
  VendorConnectionType,
} from "../../models";
import { useFunctionalityVendors } from "../../pages/hooks";
import { useFunctionalityInputChecker } from "../../pages/hooks/functionalityInputChecker";
import {
  isValidUSPhoneNumber,
  normalizeUSPhoneNumberToE164,
} from "../../utils/phoneNumberFormatting";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  parentCompanyId: string;
  pagedVendorUrl: string;
};

export function ModalCreateVendor(props: Props): JSX.Element {
  const { parentCompanyId, handleShow, show } = props;
  const { profile } = useAppSelector((state: VRIAppStateType) => state.auth);
  const { onAddVendor } = useFunctionalityVendors({
    parentCompanyId: parentCompanyId ? parentCompanyId : profile?.companyId,
    handleShow: handleShow,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ApiSchemaCreateVendor>();

  const { useFunctionlityInputFieldValidation } =
    useFunctionalityInputChecker();

  const selectedOverflowType = watch("vendorOverflowType");

  const isValidSipAddress = (value: string) => {
    return /^sip:[a-zA-Z0-9._%+\-]+@((\d{1,3}\.){3}\d{1,3}|[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+)$/i.test(
      value
    );
  };

  const validateOverflowValue = (value: string | undefined) => {
    const trimmed = (value ?? "").trim();

    if (!trimmed) {
      return "Overflow value is required";
    }

    if (selectedOverflowType === undefined || selectedOverflowType === null) {
      return true;
    }

    if (selectedOverflowType === VendorConnectionType.PhoneNumber) {
      if (!isValidUSPhoneNumber(trimmed)) {
        return "Please enter a valid US phone number (e.g. 1-313-481-4998).";
      }
    } else if (selectedOverflowType === VendorConnectionType.SipUrl) {
      if (!isValidSipAddress(trimmed)) {
        return "Please enter a valid SIP address (e.g. sip:user@example.com or sip:user@10.0.0.1).";
      }
    }

    return true;
  };

  const onCreateHandler = (form: ApiSchemaCreateVendor) => {
    const overflowType = form.vendorOverflowType;
    let overflowValue = (form.vendorOverflowValue ?? "").trim();

    if (overflowType === VendorConnectionType.PhoneNumber) {
      const normalized = normalizeUSPhoneNumberToE164(overflowValue);
      overflowValue = normalized ?? overflowValue;
    }

    form = {
      ...form,
      name: form.name.trim(),
      parentCompanyId: parentCompanyId
        ? parentCompanyId
        : profile?.companyId!,
      phoneNumber: "",
      vendorOverflowValue: overflowValue,
    };

    onAddVendor(form);
  };

  return (
    <Modal
      show={show}
      onHide={() => handleShow(false)}
      centered
      id="createVendorModal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Create Vendor</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(onCreateHandler)}>
        <Modal.Body>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label
                  htmlFor=""
                  className="position-relative required"
                >
                  Name
                </label>
                <input
                  type="text"
                  {...register("name", {
                    required: true,
                    validate: useFunctionlityInputFieldValidation,
                  })}
                  className="form-control"
                  placeholder="Name"
                />
                {errors.name?.type === "required" && (
                  <FormErrorMessage message="required" />
                )}
                {errors.name?.type === "validate" && (
                  <FormErrorMessage
                    message={InputFieldValidationErrorMessage.ErrorMessage}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label
                  htmlFor=""
                  className="position-relative required"
                >
                  Overflow Type
                </label>
                <select
                  className="form-control"
                  {...register("vendorOverflowType", {
                    required: true,
                    valueAsNumber: true,
                  })}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select overflow type
                  </option>
                  {[
                    VendorConnectionType.PhoneNumber,
                    VendorConnectionType.SipUrl,
                  ].map((v) => (
                    <option key={v} value={v}>
                      {VendorConnectionType[v]}
                    </option>
                  ))}
                </select>

                {errors.vendorOverflowType && (
                  <FormErrorMessage message="required" />
                )}
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label
                  htmlFor=""
                  className="position-relative required"
                >
                  Overflow Value
                </label>
                <input
                  type="text"
                  {...register("vendorOverflowValue", {
                    validate: validateOverflowValue,
                  })}
                  className="form-control"
                  placeholder={
                    selectedOverflowType === VendorConnectionType.PhoneNumber
                      ? "Phone number (e.g. 1-313-481-4998)"
                      : selectedOverflowType === VendorConnectionType.SipUrl
                      ? "SIP address (e.g. sip:user@example.com or sip:user@10.0.0.1)"
                      : "Overflow Value"
                  }
                />
                {errors.vendorOverflowValue && (
                  <FormErrorMessage
                    message={
                      (errors.vendorOverflowValue.message as string) ||
                      "Overflow value is required"
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-uppercase text-site"
            onClick={() => handleShow(false)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase login-from-submit-btn"
          >
            Create
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
